/** Dialogs da aba Histórico: detalhes da fatura, marcar pago e estornar. */
import type { ReactNode } from 'react';
import type { Invoice, InvoiceStatus } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Badge, Dialog } from '@/components/ui/misc';
import { ExternalLink } from 'lucide-react';
import { formatDateTime } from '@/lib/utils';
import { errorMessage } from './billing-queries';
import { usePayInvoice, useReverseInvoice } from './billing-mutations';
import { formatBRL, formatCompetence, formatDateBR } from './billing-format';
import { paymentProviderLabel, paymentStatusChip } from './asaas';

export const INVOICE_STATUS_CHIP: Record<
  InvoiceStatus,
  { label: string; variant: 'info' | 'success' | 'default' }
> = {
  billed: { label: 'Faturado', variant: 'info' },
  paid: { label: 'Pago', variant: 'success' },
  reversed: { label: 'Estornado', variant: 'default' },
};

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[13px] text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-sm break-words">{children}</div>
    </div>
  );
}

export function InvoiceDetailDialog({
  invoice,
  onClose,
}: {
  invoice: Invoice;
  onClose: () => void;
}) {
  const chip = INVOICE_STATUS_CHIP[invoice.status];
  // Cobrança (v9 Etapa 2) — campos payment* provider-genéricos do InvoiceSchema.
  const payChip = invoice.paymentStatus
    ? paymentStatusChip(invoice.paymentProvider, invoice.paymentStatus)
    : null;
  return (
    <Dialog open onClose={onClose} title={`Fatura — ${invoice.contractName}`} className="sm:max-w-lg">
      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <Field label="Cliente">{invoice.clientName}</Field>
        <Field label="Contrato">{invoice.contractName}</Field>
        <Field label="Competência">
          <span className="tabular-nums">{formatCompetence(invoice.competence)}</span>
        </Field>
        <Field label="Status">
          <Badge variant={chip.variant}>{chip.label}</Badge>
        </Field>
        <Field label="Data de faturamento">
          <span className="tabular-nums">{formatDateBR(invoice.billingDate)}</span>
        </Field>
        <Field label="Vencimento">
          <span className="tabular-nums">{formatDateBR(invoice.dueDate)}</span>
        </Field>
        <Field label="Valor base">
          <span className="tabular-nums">{formatBRL(invoice.baseValue)}</span>
        </Field>
        <Field label="Desconto / Juros">
          <span className="tabular-nums">
            {formatBRL(invoice.discountValue)} / {formatBRL(invoice.interestValue)}
          </span>
        </Field>
        <Field label="Valor Real">
          <span className="font-semibold tabular-nums">{formatBRL(invoice.realValue)}</span>
        </Field>
        <Field label="Número NFe">
          {invoice.nfeNumber ? (
            <span className="tabular-nums">{invoice.nfeNumber}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </Field>
        {invoice.paidAt ? <Field label="Pago em">{formatDateTime(invoice.paidAt)}</Field> : null}
        <Field label="Criada em">{formatDateTime(invoice.createdAt)}</Field>
        {invoice.externalPaymentId || invoice.paymentUrl || payChip ? (
          <div className="sm:col-span-2">
            <Field
              label={
                invoice.paymentProvider
                  ? `Cobrança ${paymentProviderLabel(invoice.paymentProvider)}`
                  : 'Cobrança'
              }
            >
              <span className="flex flex-wrap items-center gap-2">
                {payChip ? <Badge variant={payChip.variant}>{payChip.label}</Badge> : null}
                {invoice.externalPaymentId ? (
                  <span className="font-mono text-xs text-muted-foreground">
                    {invoice.externalPaymentId}
                  </span>
                ) : null}
                {invoice.paymentUrl ? (
                  <a
                    href={invoice.paymentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    Ver cobrança
                    <ExternalLink className="size-3.5" />
                  </a>
                ) : null}
              </span>
            </Field>
          </div>
        ) : null}
        {invoice.consumptionSummary ? (
          <div className="sm:col-span-2">
            <Field label="Consumo">{invoice.consumptionSummary}</Field>
          </div>
        ) : null}
        <div className="sm:col-span-2">
          <Field label="Observações">
            {invoice.observations ? (
              <span className="whitespace-pre-wrap">{invoice.observations}</span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )}
          </Field>
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Fechar
        </Button>
      </div>
    </Dialog>
  );
}

export function PayInvoiceDialog({ invoice, onClose }: { invoice: Invoice; onClose: () => void }) {
  const pay = usePayInvoice();
  return (
    <Dialog open onClose={onClose} title="Marcar como pago">
      <p className="text-sm text-muted-foreground">
        Confirmar o pagamento da fatura de{' '}
        <span className="font-medium text-foreground">{invoice.clientName}</span> — contrato{' '}
        {invoice.contractName}, competência {formatCompetence(invoice.competence)}, no valor de{' '}
        <span className="font-semibold text-foreground tabular-nums">
          {formatBRL(invoice.realValue)}
        </span>
        ?
      </p>
      {pay.error ? (
        <p className="mt-2 text-sm text-destructive">{errorMessage(pay.error)}</p>
      ) : null}
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={pay.isPending}
          onClick={() => pay.mutate(invoice.id, { onSuccess: onClose })}
        >
          {pay.isPending ? 'Confirmando…' : 'Marcar pago'}
        </Button>
      </div>
    </Dialog>
  );
}

export function ReverseInvoiceDialog({
  invoice,
  onClose,
}: {
  invoice: Invoice;
  onClose: () => void;
}) {
  const reverse = useReverseInvoice();
  return (
    <Dialog open onClose={onClose} title="Estornar fatura">
      <p className="text-sm text-muted-foreground">
        Estornar a fatura de{' '}
        <span className="font-medium text-foreground">{invoice.clientName}</span> — contrato{' '}
        {invoice.contractName}? O contrato volta para os faturamentos pendentes da competência{' '}
        <span className="font-medium text-foreground tabular-nums">
          {formatCompetence(invoice.competence)}
        </span>
        .
      </p>
      {reverse.error ? (
        <p className="mt-2 text-sm text-destructive">{errorMessage(reverse.error)}</p>
      ) : null}
      <div className="mt-4 flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          disabled={reverse.isPending}
          onClick={() => reverse.mutate(invoice.id, { onSuccess: onClose })}
        >
          {reverse.isPending ? 'Estornando…' : 'Estornar'}
        </Button>
      </div>
    </Dialog>
  );
}
